// @flow

import { Component } from '../jsti/core';
import Toggle from './Toggle';
import { uppercaseFirst } from '../classes/Helpers';
import ConditionalCheck from '../classes/ConditionalCheck';

type Notifications = {
  show: Array<string>,
  hide: Array<string>
};

type RenderType = 'once' | 'always' | 'never';
const RenderTypes = {
  ONCE: 'once',
  ALWAYS: 'always',
  NEVER: 'never'
};

/**
 * Receptor for switching visibility of a specific element on specific notifications
 *
 * Can receive multiple notifications to show. Should be an array
 * Can receive multiple notifications to hide. Should be an array
 *
 * Required: [data-show-on, data-hide-on]
 * Configurable: [data-class]
 */
export default class ToggleReceptor extends Component {
  class: string;
  notifications: Notifications;
  #identifier: ?string;
  #shouldRender: RenderType;
  #shouldFocus: boolean;
  #view: HTMLElement;
  #firstInput: ?HTMLInputElement;

  /**
   * Instantiate the component
   */
  bind(el: HTMLElement): void {
    super.bind(el);
    this.class = this.el.dataset.class || 'hidden';

    this.notifications = {
      show: JSON.parse(this.el.dataset.showOn),
      hide: JSON.parse(this.el.dataset.hideOn)
    };

    this.#identifier = this.el.dataset.identifier || null;

    this.addListeners();
    this.#shouldFocus = this.el.dataset.shouldFocus === 'true';

    if (this.#shouldFocus) {
      this.#firstInput = this.el.querySelector('input');
    }

    if (!this.el.dataset.shouldRender || ![RenderTypes.ONCE, RenderTypes.ALWAYS].includes(this.el.dataset.shouldRender)) {
      this.#shouldRender = RenderTypes.NEVER;
      return;
    }

    this.#shouldRender = this.el.dataset.shouldRender;

    if (!this.el.dataset.template) {
      this.#view = this.el.firstElementChild;
    } else {
      const template = document.createElement('div');
      template.innerHTML = JSON.parse(this.el.dataset.template);
      this.#view = template.firstElementChild;
    }
  }

  /**
   * Show the element
   */
  handleShow(): void {
    Toggle.show(this.el, this.class);
    if (this.#shouldFocus && this.#firstInput) {
      this.#firstInput.focus();
    }
    if (this.#shouldRender === RenderTypes.NEVER) return;

    if (!this.el.firstElementChild) {
      this.el.appendChild(this.#view);
      this.el.removeAttribute('aria-hidden');
    }
  }

  /**
   * Hide the element
   */
  handleHide(): void {
    Toggle.hide(this.el, this.class);
    if (this.#shouldRender !== RenderTypes.ALWAYS) return;

    while (this.el.firstChild) {
      this.el.removeChild(this.el.lastChild);
    }
    this.el.setAttribute('aria-hidden', 'true');
  }

  /**
   * Add listeners for the notifications
   */
  addListeners(): void {
    for (const key of Object.keys(this.notifications)) {
      this.notifications[key].forEach((notification: string) => {
        const parts: Array<string> = notification.split('@');
        const notificationName = parts[0];
        const notificationCheck = parts[1];

        this.addListener(notificationName, (data: Object) => {
          if (this.#identifier && data.identifier !== this.#identifier) return;

          if (notificationCheck && !this.checkIsPassing(notificationCheck, data)) return;

          this[`handle${uppercaseFirst(key)}`]();
        });
      });
    }
  }

  /**
   * Check if the notification check is passing
   */
  checkIsPassing(check: string, data: Object): boolean {
    const parameters = check.split('.');
    if (parameters.length < 2) return false;

    const checkParameter = parameters[1];
    const comparisonParameter = data[parameters[0]];

    if ((!['isNull', 'isNotNull'].includes(checkParameter) && [null, undefined].includes(comparisonParameter))) return false;

    return ConditionalCheck.passes({
      data: parameters[2] || null,
      check: checkParameter,
      comparison: comparisonParameter
    });
  }

  /**
   * Getter for the view property
   */
  get view(): HTMLElement {
    return this.#view;
  }
}
